import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { api } from "../../api";
import { notifyError, notifySuccess } from "../Notification";
import { FiAlertCircle, FiDownload, FiFileText, FiPrinter } from 'react-icons/fi';
import { QRCode } from "react-qr-code";
import jsPDF from "jspdf";
import { useRef } from "react";
import Header from "../header/header";
import SuperAdminBusiness from "../superAdmin/SuperAdminBusinessControl";
import SuperAdminStore from "../superAdmin/superAdminStoreControl";
import GenericModal from "../genericModal/GenericModal";
import Button from "../Button/Button";
import Decks from "../decks/Decks";
import { fetchUserBusinesses } from "../../redux/userBusinessesSlice";
import { fetchDecks, createDeck } from '../../redux/decksSlice';
import ModernInviteModal from "../ModernInviteModal/ModernInviteModal";
import EnhancedUsersTable from './EnhancedUsersTable';
import AccessCardLoader from './AccessCardSpinner';
import UnifiedDropdown from "../toggles/Dropdown/Dropdown";
import "./admin.css";
import { addBusiness } from '../../redux/businessesSlice';
import IconButton from "../Button/IconButton";
import QRCodeExportUtils from './QRCodeExportUtils';

// Safe localStorage access for selectedBusinessId
const getSelectedBusinessIdFromStorage = () => {
  try {
    return localStorage.getItem('selectedBusinessId');
  } catch (error) {
    console.error('Error accessing localStorage for selectedBusinessId:', error);
    return null;
  }
};

// Safe localStorage setter for selectedBusinessId
const setSelectedBusinessIdToStorage = (businessId) => {
  try {
    if (businessId) {
      localStorage.setItem('selectedBusinessId', businessId);
    } else {
      localStorage.removeItem('selectedBusinessId');
    }
  } catch (error) {
    console.error('Error setting localStorage for selectedBusinessId:', error);
  }
};

// Safe localStorage setter for selectedBusinessName
const setSelectedBusinessNameToStorage = (businessName) => {
  try {
    if (businessName) {
      localStorage.setItem('selectedBusinessName', businessName);
    } else {
      localStorage.removeItem('selectedBusinessName');
    }
  } catch (error) {
    console.error('Error setting localStorage for selectedBusinessName:', error);
  }
};

const BusinessStoreSelector = () => {
  const navigate = useNavigate();
  
  // Simplified loading state - single source of truth
  const [isAppReady, setIsAppReady] = useState(false);
  const [loadingStep, setLoadingStep] = useState('Initializing...');
  
  // Component state
  const [selectedBusiness, setSelectedBusiness] = useState("All");
  const [selectedStore, setSelectedStore] = useState("All");
  const [stores, setStores] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [currentView, setCurrentView] = useState("site");
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [deckCounts, setDeckCounts] = useState({ total: 0, running: 0 });
  const [currentDeckView, setCurrentDeckView] = useState("cards");
  const [showCreateDeckModal, setShowCreateDeckModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showBusinessAccessModal, setShowBusinessAccessModal] = useState(false);
  const [showStoreAccessModal, setShowStoreAccessModal] = useState(false);

  // Create new deck state
  const [deckName, setDeckName] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [showRemoveUserModal, setShowRemoveUserModal] = useState(false);
  const [userToRemove, setUserToRemove] = useState(null);
  const [showAddBusinessModal, setShowAddBusinessModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [qrDeviceName, setQrDeviceName] = useState("");
  const [newBusinessName, setNewBusinessName] = useState("");
  const [qrType, setQrType] = useState("feedback"); // "feedback" or "polling"
  const [pollingQRCodeUrl, setPollingQRCodeUrl] = useState("");
  const [pollingConfigExists, setPollingConfigExists] = useState(false); // Track if polling config exists
  const [pollingEventDates, setPollingEventDates] = useState({ startDate: null, endDate: null });
  const [isEditingDates, setIsEditingDates] = useState(false);
  const [editableDates, setEditableDates] = useState({ startDate: '', endDate: '' });
  const [isSavingDates, setIsSavingDates] = useState(false);
  const [currentPollId, setCurrentPollId] = useState(null);

  const qrCodeRef = useRef(null);

  // Modal handlers
  const handleShow = () => setShowModal(true);
  const handleClose = () => setShowModal(false);

  // Redux state
  const { items: userBusinesses, loading, error } = useSelector((state) => state.userBusinesses);
  const { items: decks, loadingFetchDecks } = useSelector((state) => state.decks);
  const dispatch = useDispatch();
  const userEmail = localStorage.getItem("user");

  // Modal handlers
  const handleBusinessAccessModalClose = () => setShowBusinessAccessModal(false);
  const handleBusinessAccessModalShow = () => setShowBusinessAccessModal(true);
  
  const handleStoreAccessModalClose = () => setShowStoreAccessModal(false);
  const handleStoreAccessModalShow = () => setShowStoreAccessModal(true);

  // Date editing handler
  const handleSavePollingDates = async () => {
    if (!currentPollId) return;

    // Validate dates
    if (editableDates.startDate && editableDates.endDate) {
      const startDate = new Date(editableDates.startDate);
      const endDate = new Date(editableDates.endDate);
      if (startDate > endDate) {
        notifyError('End date must be after start date');
        return;
      }
    }

    setIsSavingDates(true);
    try {
      const updateData = {
        start_date: editableDates.startDate ? new Date(editableDates.startDate).toISOString() : null,
        end_date: editableDates.endDate ? new Date(editableDates.endDate).toISOString() : null
      };

      await api.put(`/polls/${currentPollId}`, updateData);

      setPollingEventDates({
        startDate: updateData.start_date,
        endDate: updateData.end_date
      });

      setIsEditingDates(false);
      notifySuccess('Polling event dates updated successfully');
      console.log('✅ Polling dates updated:', currentPollId);
    } catch (error) {
      console.error('Error updating polling dates:', error);
      notifyError(error.response?.data?.message || 'Failed to update polling dates');
    } finally {
      setIsSavingDates(false);
    }
  };

  // Device modal states
  const [showStandardDevicesModal, setShowStandardDevicesModal] = useState(false);
  const [showVirtualDevicesModal, setShowVirtualDevicesModal] = useState(false);
  const [selectedSiteForDevices, setSelectedSiteForDevices] = useState(null);
  const [showAddVirtualDeviceModal, setShowAddVirtualDeviceModal] = useState(false);
  const [virtualKioskName, setVirtualKioskName] = useState("");

  // Calculate device totals from actual device data
  const calculateDeviceTotals = () => {
    const totals = { standard: 0, virtual: 0 };
    
    stores.forEach(store => {
      if (store.devices && Array.isArray(store.devices)) {
        store.devices.forEach(device => {
          if (device.type === 'standard') {
            totals.standard++;
          } else if (device.type === 'virtual') {
            totals.virtual++;
          }
        });
      }
    });
    
    return totals;
  };

  // Get devices by type for a specific store
  const getDevicesByType = (store, deviceType) => {
    if (!store.devices || !Array.isArray(store.devices)) {
      return [];
    }
    
    return store.devices.filter(device => device.type === deviceType);
  };

  const getDeckByStoreId = (decks, storeId) => {
  if (!Array.isArray(decks) || !storeId) return null;

  for (const deck of decks) {
    if (deck.storeIds?.includes(storeId)) {
      return {
        deckId: deck.deckId,
        deckName: deck.deckName,
        deckQuestion: deck.deckQuestion
      };
    }
  }

  return null;
};

  // Generate QR code URL for virtual devices - FEEDBACK
  const generateQRCodeUrl = (
    businessId,
    storeId,
    deviceType,
    deviceId,
    deviceName
  ) => {

    // Find deck assigned to this store
    const deckInfo = getDeckByStoreId(decks, storeId);

    const baseUrl = process.env.REACT_APP_QR_CODE_BASE_URL;

    const params = new URLSearchParams({
      device_type: deviceType,
      business_id: businessId,
      store_id: storeId,
      device_id: deviceId,
      device_name: deviceName,
      deck_id: deckInfo?.deckId || "",
      deck_name: deckInfo?.deckName || "",
      deck_question: deckInfo?.deckQuestion || "",
      mode: "feedback"
    });
    console.log("Generated Feedback QR Code URL Params:", params.toString());
    return `${baseUrl}/#device-access?${params.toString()}`;
  };

  // Generate QR code URL for polling
  const generatePollingQRCodeUrl = (
    businessId,
    storeId,
    deviceType,
    deviceId,
    deviceName
  ) => {

    // Find deck assigned to this store
    const deckInfo = getDeckByStoreId(decks, storeId);

    const baseUrl = process.env.REACT_APP_QR_CODE_BASE_URL;

    const params = new URLSearchParams({
      device_type: deviceType,
      business_id: businessId,
      store_id: storeId,
      device_id: deviceId,
      device_name: deviceName,
      deck_id: deckInfo?.deckId || "",
      deck_name: deckInfo?.deckName || "",
      mode: "polling"
    });
    console.log("Generated Polling QR Code URL Params:", params.toString());
    return `${baseUrl}/#device-access?${params.toString()}`;
  };


  // Device modal handlers
  const handleShowStandardDevices = (store) => {
    setSelectedSiteForDevices(store);
    setShowStandardDevicesModal(true);
  };

  const handleShowVirtualDevices = (store) => {
    setSelectedSiteForDevices(store);
    setShowVirtualDevicesModal(true);
  };

  const handleShowAddVirtualDeviceModal = () => {
    setShowAddVirtualDeviceModal(true);
  };

  const handleCreateVirtualDevice = async () => {
  if (!virtualKioskName.trim()) {
    notifyError("Please enter a virtual kiosk name.");
    return;
  }

  try {
    const response = await api.post("/deviceQR", {
      store_id: selectedSiteForDevices.store_id,
      device_name: virtualKioskName,
      device_type: "virtual",
    });

    if (response.data.success) {
      notifySuccess("Virtual device created successfully!");
      setShowAddVirtualDeviceModal(false);
      setVirtualKioskName("");

      // Refresh user businesses to get updated device data
      dispatch(fetchUserBusinesses(userEmail));
    } else {
      notifyError(response.data.message || "Error creating virtual device");
    }
  } catch (error) {
    console.error("Error creating virtual device:", error);
    
    // Handle HTTP error responses
    if (error.response && error.response.data) {
      const errorData = error.response.data;
      
      // Check for virtual seats limit error
      if (errorData.error_type === 'VIRTUAL_SEATS_LIMIT_REACHED') {
        notifyError("No Virtual seats available! Contact support for assistance.");
      } else {
        // Handle other API errors
        notifyError(errorData.message || "Error creating virtual device");
      }
    } else {
      // Handle network/connection errors
      notifyError("An error occurred while creating the virtual device. Please check your connection and try again.");
    }
  }
};

  const handleAddBusiness = async () => {
    if (!newBusinessName.trim()) {
      notifyError("Please enter a business name.");
      return;
    }

    try {
      await dispatch(addBusiness({ business_name: newBusinessName, email: userEmail })).unwrap();
      notifySuccess("Business added successfully!");
      setShowAddBusinessModal(false);
      setNewBusinessName("");
      dispatch(fetchUserBusinesses(userEmail));
    } catch (error) {
      notifyError(error.message || "Error adding business");
    }
  };

  const handleConfirmRemoveUser = async () => {
    console.log('Confirming removal of user:', userToRemove);
    if (userToRemove) {
      try {
        const response = await api.delete(`/access/remove-user/${userToRemove}?business_id=${selectedBusiness}`);
        if (response.data.success) {
          notifySuccess("User removed successfully");
          fetchUsers();
        }
      } catch (error) {
        console.error("Error removing user:", error);
        const errorMessage = error.response?.data?.message || "Failed to remove user";
        notifyError(errorMessage);
      } finally {
        setShowRemoveUserModal(false);
        setUserToRemove(null);
      }
    }
  };

  const handleCancelRemoveUser = () => {
    setShowRemoveUserModal(false);
    setUserToRemove(null);
  };

  // Consolidated data loading effect
  useEffect(() => {
    const loadAllAppData = async () => {
      try {
        setLoadingStep('Loading user profile...');
        
        const userProfilePromise = fetchUserRole();
        
        setLoadingStep('Loading businesses...');
        const businessesPromise = dispatch(fetchUserBusinesses(userEmail)).unwrap();
        
        const [userProfileData, businessesData] = await Promise.all([
          userProfilePromise,
          businessesPromise
        ]);
        
        setLoadingStep('Setting up business selection...');
        
        let selectedBusinessId = null;
        
        if (businessesData && businessesData.length > 0) {
          await new Promise((resolve) => {
            const savedBusinessId = getSelectedBusinessIdFromStorage();
            let defaultBusinessId = '';
            let defaultBusiness = null;
            
            if (savedBusinessId) {
              const savedBusinessExists = businessesData.find(b => b.business_id === parseInt(savedBusinessId));
              if (savedBusinessExists) {
                defaultBusinessId = savedBusinessId;
                defaultBusiness = savedBusinessExists;
              } else {
                defaultBusinessId = businessesData[0].business_id.toString();
                defaultBusiness = businessesData[0];
              }
            } else {
              defaultBusinessId = businessesData[0].business_id.toString();
              defaultBusiness = businessesData[0];
            }
            
            setSelectedBusiness(defaultBusinessId);
            setSelectedBusinessIdToStorage(defaultBusinessId);
            setSelectedBusinessNameToStorage(defaultBusiness.business_name);
            setStores(defaultBusiness?.stores || []);
            setSelectedStore(defaultBusiness?.stores?.length ? defaultBusiness.stores[0].store_id : "All");
            
            selectedBusinessId = defaultBusinessId;
            
            setTimeout(resolve, 100);
          });
        }
        
        setLoadingStep('Loading decks...');
        
        if (selectedBusinessId) {
          try {
            await dispatch(fetchDecks({ 
              userEmail, 
              selectedBusinessId 
            })).unwrap();
          } catch (deckError) {
            console.error('Error loading decks:', deckError);
          }
        }
        
        setLoadingStep('Finalizing...');
        
        await new Promise(resolve => setTimeout(resolve, 300));
        
        setIsAppReady(true);
        
      } catch (error) {
        console.error('Error loading app data:', error);
        setLoadingStep('Error loading data');
      }
    };

    if (userEmail && !isAppReady) {
      loadAllAppData();
    }
  }, [userEmail, dispatch, isAppReady]);

  // Fetch user role function
  const fetchUserRole = async () => {
    try {
      const { data } = await api.post('/user', { 
        email: localStorage.getItem("user"),
        business_id: localStorage.getItem("selectedBusinessId") 
      });
      
      if (data) {
        localStorage.setItem("userProfile", JSON.stringify(data));
        setUserProfile(data);
        return data;
      }
    } catch (error) {
      console.error("Error fetching user role:", error.response ? error.response.statusText : error.message);
      notifyError("Internal Server Error. Please try again later.");
      throw error;
    }
  };

  // Load users when business changes
  useEffect(() => {
    if (selectedBusiness && selectedBusiness !== "All" && isAppReady) {
      fetchUsers();
    }
  }, [selectedBusiness, isAppReady]);

  // Enhanced view switching to handle data loading
  const switchView = (view) => {
    setCurrentView(view);
    
    if (view === 'access') {
      fetchUsers();
    } else if (view === 'deck') {
      if (!decks.length && selectedBusiness && selectedBusiness !== "All") {
        dispatch(fetchDecks({ 
          userEmail, 
          selectedBusinessId: selectedBusiness 
        }));
      }
    }
  };

  // Enhanced business change handler
  const handleBusinessChange = (event) => {
    const businessId = event.target.value;
    const selected = userBusinesses.find((business) => business.business_id === Number(businessId));
    
    setSelectedBusiness(businessId);
    
    setSelectedBusinessIdToStorage(businessId);
    if (selected) {
      setSelectedBusinessNameToStorage(selected.business_name);
    }
    
    setStores(selected ? selected.stores : []);
    setSelectedStore(selected && selected.stores.length ? selected.stores[0].store_id : "All");
    
    if (businessId && businessId !== "All") {
      dispatch(fetchDecks({ 
        userEmail, 
        selectedBusinessId: businessId 
      }));
    }
  };

  const handleBusinessChangeUnified = (businessId) => {
    const selected = userBusinesses.find((business) => business.business_id === Number(businessId));
    
    setSelectedBusiness(businessId);
    
    setSelectedBusinessIdToStorage(businessId);
    if (selected) {
      setSelectedBusinessNameToStorage(selected.business_name);
    }
    
    setStores(selected ? selected.stores : []);
    setSelectedStore(selected && selected.stores.length ? selected.stores[0].store_id : "All");
    
    if (businessId && businessId !== "All") {
      dispatch(fetchDecks({ 
        userEmail, 
        selectedBusinessId: businessId 
      }));
    }
  };

  const handleStoreChange = (event) => {
    const storeId = event.target.value;
    setSelectedStore(storeId);
  };

  const handleNavigateToSite = (store) => {
    console.log('Navigating to site dashboard for:', store.store_name, 'ID:', store.store_id);
    
    setSelectedBusinessIdToStorage(selectedBusiness);
    
    navigate(`/site/${store.store_id}`);
  };

  useEffect(() => {
    const handleDeckCountUpdate = (event) => {
      setDeckCounts(event.detail);
    };
    
    window.addEventListener('deckCountUpdate', handleDeckCountUpdate);
    
    return () => {
      window.removeEventListener('deckCountUpdate', handleDeckCountUpdate);
    };
  }, []);

  const fetchUsers = async () => {
    try {
      console.log('Fetching users for business:', selectedBusiness);
      const response = await api.get(`/access?business_id=${selectedBusiness}`);
      
      console.log('API response:', response.data); // Debug log
      
      // Check if response has the expected structure
      if (response?.data?.success && Array.isArray(response?.data?.data)) {
        setUsers(response.data.data); // Extract the users array
        console.log('Users set:', response.data.data.length, 'users');
      } else {
        console.warn('Unexpected response structure:', response.data);
        setUsers([]); // Fallback to empty array
        //notifyError("Unexpected response format from server");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]); // Reset to empty array on error
      
      // More specific error handling
      if (error.response?.status === 403) {
        notifyError("You don't have permission to view users for this business");
      } else if (error.response?.status === 404) {
        notifyError("No users found for this business");
      } else {
        notifyError("Failed to fetch users");
      }
    }
  };

  const handleInviteUser = async ({ email, role, businessId }) => {
    console.log('Inviting user:', email, role, businessId);
    try {
      const response = await api.post('/invite/send', {
        email,
        role,
        businessId
      });

      if (response.data.success) {
        notifySuccess(`Invitation sent successfully to ${email}`);
        fetchUsers();
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to send invitation');
      }
    } catch (error) {
      console.error("Error inviting user:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to invite user";
      notifyError(errorMessage);
      throw error;
    }
  };

  const handleRemoveUser = (user) => {
    console.log('Removing user:', user);
    setUserToRemove(user);
    setShowRemoveUserModal(true);
  };

  const handleChangeUserRole = async (userEmail, newRole) => {
    try {
      const response = await api.put(`/access/change-role/${userEmail}`, { role: newRole });
      if (response.data.success) {
        notifySuccess("User role updated successfully");
        fetchUsers();
      }
    } catch (error) {
      console.error("Error changing user role:", error);
      const errorMessage = error.response?.data?.message || "Failed to change user role";
      notifyError(errorMessage);
    }
  };

  const hasAdminPermissions = () => {
    console.log(userProfile?.role);
    return userProfile?.role === 'R1' || userProfile?.role === 'TLA' || userProfile?.role === 'Business_Admin' || userProfile?.role === 'Admin';
  };

  const handleCreateDeck = () => {
    if (!deckName.trim()) {
      alert("Please enter a deck name.");
      return;
    }

    const selectedBusinessId = localStorage.getItem("selectedBusinessId");
    dispatch(createDeck({ deckName, selectedBusinessId }))
      .unwrap()
      .then(() => {
        notifySuccess(`Deck ${deckName} created successfully`);
        const userEmail = localStorage.getItem('user');
        dispatch(fetchDecks({
          userEmail,
          selectedBusinessId: localStorage.getItem("selectedBusinessId")
        }));
      })
      .catch((error) => {
        notifyError(`Failed to create deck: ${error.message}`);
      });
    setShowModal(false);
    setDeckName("");
    handleClose();
  };

  const showSpinner = !isAppReady;
  const deviceTotals = calculateDeviceTotals();

  return (
    <div className="admin-container" style={{ position: 'relative' }}>
      <Header />
      
      <div className="admin-main-content" style={{
        filter: showSpinner ? 'blur(3px)' : 'none',
        opacity: showSpinner ? 0.7 : 1,
        transition: 'filter 0.5s ease-out, opacity 0.3s ease-out'
      }}>
        {/* Header Section */}
        <div className="admin-header">
          <h1>Admin Controls</h1>
          <div className="view-toggle">
            <button 
              className={currentView === 'site' ? 'active' : ''} 
              onClick={() => switchView('site')}
            >
              Sites
            </button>
            <button 
              className={currentView === 'deck' ? 'active' : ''} 
              onClick={() => switchView('deck')}
            >
              Decks
            </button>
            <button 
              className={currentView === 'access' ? 'active' : ''} 
              onClick={() => switchView('access')}
            >
              Users
            </button>
          </div>
        </div>

        {/* Filters Section */}
        <div className="admin-filters">
          <div className="filter-group">
            <label>Business:</label>
            {userBusinesses.length ? (
              <UnifiedDropdown
                type="business"
                selectedValue={selectedBusiness}
                setSelectedValue={handleBusinessChangeUnified}
                options={userBusinesses.map(b => b.business_id)}
                displayMapping={userBusinesses.reduce((acc, b) => ({ ...acc, [b.business_id]: b.business_name }), {})}
              />
            ) : (
              <div className="no-business-message">
                No businesses available
              </div>
            )}
           
            <IconButton 
              icon="add" 
              tooltip="Add New Business"
              variant="secondary"
              onClick={() => setShowAddBusinessModal(true)} 
            />
          </div>

          <div className="search-box">
            <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
            </svg>
            <input
              type="text"
              placeholder="Search decks or sites..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Content Area */}
        <div className="admin-content-area">
          {currentView === 'deck' ? (
            <div className="deck-table-container">
              <div className="deck-view-toolbar">
                <div className="bulk-actions">
                  <span className="selected-count">{deckCounts.total} decks total • {deckCounts.running} running</span>
                </div>
                <Button variant="primary" onClick={handleShow}>
                  <svg 
                    width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
                  </svg>
                   Add Deck
                </Button>
              </div>
              <Decks selectedBusiness={selectedBusiness} />
            </div>
          ) : currentView === 'site' ? (
            <div className="site-view-container">
              {/* Site View Content */}
              <div className="site-view-toolbar">
                <div className="bulk-actions">
                  <span className="selected-count">
                    {stores.length} sites total • {stores.filter(s => s.status === 'online' || s.status === 'running').length} online
                  </span>
                  
                  <div className="site-view-toolbar-right">
                  <div className="status-filters">
                    <button 
                      className={`status-filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
                      onClick={() => setStatusFilter('all')}
                    >
                      All
                    </button>
                    <button 
                      className={`status-filter-btn ${statusFilter === 'online' ? 'active' : ''}`}
                      onClick={() => setStatusFilter('online')}
                    >
                      <span className="filter-dot online"></span>
                      Online
                    </button>
                    <button 
                      className={`status-filter-btn ${statusFilter === 'issues' ? 'active' : ''}`}
                      onClick={() => setStatusFilter('issues')}
                    >
                      <span className="filter-dot issues"></span>
                      Issues
                    </button>
                    <button 
                      className={`status-filter-btn ${statusFilter === 'offline' ? 'active' : ''}`}
                      onClick={() => setStatusFilter('offline')}
                    >
                      <span className="filter-dot offline"></span>
                      Offline
                    </button>
                  </div>
                  
                </div>
                <div className="device-totals-container">
                      <span className="selected-count">standard devices total • {deviceTotals.standard}</span>
                      <span className="selected-count">virtual devices total • {deviceTotals.virtual}</span>
                  </div>
                </div>
                
                <Button size="small" variant="primary" onClick={handleStoreAccessModalShow}>
                <svg 
                  width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
                </svg>
                  Add Site
                </Button>
              </div>

              <div className="sites-table-container">
                <table className="sites-table">
                  <thead>
                    <tr>
                      <th>Site Name</th>
                      <th>Status</th>
                      <th>Standard Devices</th>
                      <th>Virtual Devices</th>
                      <th>Dashboard</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stores.filter(store => {
                      const matchesSearch = !searchTerm || 
                        store.store_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        store.store_id?.toString().toLowerCase().includes(searchTerm.toLowerCase());
                      
                      if (statusFilter === 'all') return matchesSearch;
                      
                      const statusOptions = ['online', 'offline', 'issues'];
                      const siteStatus = store.status || statusOptions[Math.floor(Math.random() * statusOptions.length)];
                      
                      const matchesStatus = statusFilter === siteStatus;
                      
                      return matchesSearch && matchesStatus;
                    }).map((store) => {
                      const statusOptions = ['online', 'offline', 'issues'];
                      const siteStatus = store.status || statusOptions[Math.floor(Math.random() * statusOptions.length)];
                      
                      const standardDeviceCount = getDevicesByType(store, 'standard').length;
                      const virtualDeviceCount = getDevicesByType(store, 'virtual').length;

                      return (
                        <tr key={store.store_id} className="site-row">
                          <td className="site-name-cell">
                            <span className="site-name-text">{store.store_name}</span>
                          </td>
                          
                          <td className="site-status-cell">
                            <span className={`status-badge-inline status-${siteStatus}`}>
                              <svg width="6" height="6" fill="currentColor" viewBox="0 0 8 8">
                                <circle cx="4" cy="4" r="4"/>
                              </svg>
                              {siteStatus === 'online' ? 'Online' : 
                               siteStatus === 'offline' ? 'Offline' : 'Issues Detected'}
                            </span>
                          </td>
                          
                          <td className="device-count-cell">
                            <div className="device-count-container">
                              <span className="device-count-number">{standardDeviceCount}</span>
                              <button 
                                title="View Standard Devices"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShowStandardDevices(store);
                                }}
                              >
                                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                                </svg>
                              </button>
                            </div>
                          </td>
                          
                          <td className="device-count-cell">
                            <div className="device-count-container">
                              <span className="device-count-number">{virtualDeviceCount}</span>
                              <button
                                title="View Virtual Devices"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShowVirtualDevices(store);
                                }}
                              >
                                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                                </svg>
                              </button>
                            </div>
                          </td>
                          
                          <td className="dashboard-cell">
                            <button 
                              className="btn btn-primary-outline" 
                              title="View Dashboard"
                              onClick={() => handleNavigateToSite(store)}
                            >
                              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
                              </svg>
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                
                {stores.filter(store => {
                  const matchesSearch = !searchTerm || 
                    store.store_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    store.store_id?.toString().toLowerCase().includes(searchTerm.toLowerCase());
                  
                  if (statusFilter === 'all') return matchesSearch;
                  
                  const statusOptions = ['online', 'offline', 'issues'];
                  const siteStatus = store.status || statusOptions[Math.floor(Math.random() * statusOptions.length)];
                  const matchesStatus = statusFilter === siteStatus;
                  
                  return matchesSearch && matchesStatus;
                }).length === 0 && (
                  <div className="empty-state-table">
                    {searchTerm || statusFilter !== 'all' ? (
                      <p>No sites found matching current filters</p>
                    ) : (
                      <p>No sites available for the selected business</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="access-control-container">
              <div className="access-control-toolbar">
                <div className="bulk-actions">
                  <span className="selected-count">{users.length} users total</span>
                </div>
                {hasAdminPermissions() && (
                  <Button 
                    variant="primary"
                    onClick={() => setShowInviteModal(true)}
                  >
                    <svg 
                      width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"></path>
                    </svg>
                    Invite User
                  </Button>
                )}
              </div>

              <EnhancedUsersTable
                users={users}
                selectedBusiness={selectedBusiness}
                hasAdminPermissions={hasAdminPermissions}
                handleRemoveUser={handleRemoveUser}
                handleChangeUserRole={handleChangeUserRole}
                fetchUsers={fetchUsers}
                userProfile={userProfile}
              />
            </div>
          )}
        </div>
      </div>

      {showSpinner && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(241, 245, 249, 0.95)',
          backdropFilter: 'blur(10px)',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <AccessCardLoader 
            onComplete={() => {}}
            duration={2000}
          />
          <div style={{ 
            marginTop: '20px', 
            color: '#64748b', 
            fontSize: '14px',
            textAlign: 'center'
          }}>
            {loadingStep}
          </div>
        </div>
      )}

      {/* Standard Devices Modal */}
      <GenericModal 
        show={showStandardDevicesModal} 
        onClose={() => setShowStandardDevicesModal(false)}
        header={
          <div className="device-modal-header">
            <h2 className="device-modal-title">Standard Devices</h2>
            {selectedSiteForDevices && (
              <p className="device-modal-subtitle">{selectedSiteForDevices.store_name}</p>
            )}
          </div>
        }
        body={
          <div className="device-modal-content">
            <div className="device-table-container">
              <table className="device-table">
                <thead>
                  <tr>
                    <th>Device Name</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedSiteForDevices && getDevicesByType(selectedSiteForDevices, 'standard').map((device, index) => (
                    <tr key={device.device_id || index} className="device-row">
                      <td className="device-name-cell">
                        <span className="device-name-text">{device.device_name || `Device ${index + 1}`}</span>
                      </td>
                      <td className="device-status-cell">
                        <span className={`status-badge-inline status-online`}>
                          <svg width="6" height="6" fill="currentColor" viewBox="0 0 8 8">
                            <circle cx="4" cy="4" r="4"/>
                          </svg>
                          Online
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {selectedSiteForDevices && getDevicesByType(selectedSiteForDevices, 'standard').length === 0 && (
                <div className="empty-device-state">
                  <p>No standard devices found for this site</p>
                </div>
              )}
            </div>
          </div>
        }
        footer={
          <button className="btn btn-secondary" onClick={() => setShowStandardDevicesModal(false)}>
            Close
          </button>
        }
      />

      {/* Virtual Devices Modal */}
      <GenericModal 
        show={showVirtualDevicesModal} 
        onClose={() => setShowVirtualDevicesModal(false)}
        header={
          <div className="device-modal-header">
            <div className="virtual-kiosks-header">
              <h2 className="device-modal-title">Virtual Kiosks</h2>
              <button className="create-virtual-kiosk-btn" onClick={handleShowAddVirtualDeviceModal}>
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
                </svg>
                <span style={{ marginLeft: '0.5rem' }}>Create Virtual Kiosk</span>
              </button>
            </div>
            {selectedSiteForDevices && (
              <p className="device-modal-subtitle">{selectedSiteForDevices.store_name}</p>
            )}
          </div>
        }
        body={
          <div className="device-modal-content">
            <div className="device-table-container">
              <table className="device-table">
                <thead>
                  <tr>
                    <th>Device Name</th>
                    <th>Status</th>
                    <th>View QR</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedSiteForDevices && getDevicesByType(selectedSiteForDevices, 'virtual').map((device, index) => (
                    <tr key={device.device_id || index} className="device-row">
                      <td className="device-name-cell">
                        <span className="device-name-text">{device.device_name || `Virtual Device ${index + 1}`}</span>
                      </td>
                      <td className="device-status-cell">
                        <span className={`status-badge-inline status-online`}>
                          <svg width="6" height="6" fill="currentColor" viewBox="0 0 8 8">
                            <circle cx="4" cy="4" r="4"/>
                          </svg>
                          Online
                        </span>
                      </td>
                      <td className="device-action-cell">
                        <button
                          className="btn btn-secondary-outline btn-small"
                          onClick={async () => {
                            const feedbackQrUrl = generateQRCodeUrl(selectedBusiness, selectedSiteForDevices.store_id, device.type, device.device_id, device.device_name);

                            // Check if polling configuration exists for this store's deck
                            const deckInfo = getDeckByStoreId(decks, selectedSiteForDevices.store_id);
                            let pollingExists = false;
                            let pollingQrUrl = "";

                            if (deckInfo?.deckId) {
                              try {
                                const response = await api.get(`/polls/${deckInfo.deckId}`);
                                if (response.data?.data) {
                                  pollingExists = true;
                                  pollingQrUrl = generatePollingQRCodeUrl(selectedBusiness, selectedSiteForDevices.store_id, device.type, device.device_id, device.device_name);

                                  // Convert ISO dates to datetime-local format for editing
                                  const formatDatetimeLocal = (isoString) => {
                                    if (!isoString) return '';
                                    const date = new Date(isoString);
                                    return date.toISOString().slice(0, 16);
                                  };

                                  setPollingEventDates({
                                    startDate: response.data.data.start_date || null,
                                    endDate: response.data.data.end_date || null
                                  });

                                  setEditableDates({
                                    startDate: formatDatetimeLocal(response.data.data.start_date),
                                    endDate: formatDatetimeLocal(response.data.data.end_date)
                                  });

                                  setCurrentPollId(response.data.data.poll_id);
                                  setIsEditingDates(false);

                                  console.log('✅ Polling configuration found for deck:', deckInfo.deckId);
                                } else {
                                  console.log('⚠️ No polling configuration found for deck:', deckInfo.deckId);
                                }
                              } catch (error) {
                                console.error('Error checking polling configuration:', error);
                              }
                            }

                            setShowQRModal(true);
                            setQrCodeUrl(feedbackQrUrl);
                            setPollingQRCodeUrl(pollingQrUrl);
                            setQrDeviceName(device.device_name);
                            setPollingConfigExists(pollingExists);
                            setQrType("feedback"); // Default to feedback tab
                          }}
                        >
                          View QR
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {selectedSiteForDevices && getDevicesByType(selectedSiteForDevices, 'virtual').length === 0 && (
                <div className="empty-device-state">
                  <p>No virtual devices found for this site</p>
                </div>
              )}
            </div>
          </div>
        }
        footer={
          <button className="btn btn-secondary" onClick={() => setShowVirtualDevicesModal(false)}>
            Close
          </button>
        }
      />

      {/* Other existing modals... */}
      {/* Business Access Control Modal */}
      <GenericModal show={showBusinessAccessModal} onClose={handleBusinessAccessModalClose}
        header={<h1>Add Business</h1>}
        body={<SuperAdminBusiness />}
        footer={
          <button className="btn btn-secondary" onClick={handleBusinessAccessModalClose}>
            Close
          </button>
        }
      />

      {/* Store Access Control Modal */}
      <GenericModal show={showStoreAccessModal} onClose={handleStoreAccessModalClose}
        header={
          <div className="custom-modal-header">
            <div className="modern-edit-icon">
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"
                />
              </svg>
            </div>
            <div className="deck-modal-header-content">
              <h1 className='slide1-main-title'>Site Control</h1>
              <p className='slide1-subtitle' style={{ color: 'rgba(255, 255, 255, 0.8)' }}>Add or update your sites</p>
            </div>
          </div>
        }
        body={<SuperAdminStore users={userBusinesses} />}
      />

      {/* Modern Invite User Modal */}
      <ModernInviteModal
        showInviteModal={showInviteModal}
        setShowInviteModal={setShowInviteModal}
        userBusinesses={userBusinesses}
        selectedBusiness={selectedBusiness}
        onInviteUser={handleInviteUser}
      />

      {/* Remove User Confirmation Modal */}
      <GenericModal
        show={showRemoveUserModal}
        onClose={handleCancelRemoveUser}
        header={
          <div className="modern-modal-header">
            <button className="modern-close-btn" onClick={handleCancelRemoveUser}>
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
            <div className="modern-invite-icon" style={{ backgroundColor: '#fee2e2' }}>
              <FiAlertCircle size={28} color="#ef4444" />
            </div>
            <h2 className="modern-modal-title">Confirm User Removal</h2>
            <p className="modern-modal-subtitle">This action cannot be undone.</p>
          </div>
        }
        body={
          userToRemove && (
            <p>Are you sure you want to remove user <strong>{userToRemove}</strong>? This action cannot be undone.</p>
          )
        }
        footer={
          <>
            <Button variant="secondary" onClick={handleCancelRemoveUser}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmRemoveUser}>
              Remove User
            </Button>
          </>
        }
      />

      {/* Create Deck Modal */}
      <GenericModal show={showModal} onClose={handleClose}
        header={
          <div className="custom-modal-header">
            <div className="modern-invite-icon">
              <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
              </svg>
            </div>
            <div className="deck-modal-header-content">
              <h2 className='slide1-main-title'>Create new deck</h2>
              <p className="modern-modal-subtitle">Add your new deck with a deck name</p>
            </div>
          </div>
        }
        body={
          <div className="form-group">
            <div className="add-deck-label">Deck Name:</div>
            <input
              type="text"
              id="deckName"
              className="form-control"
              value={deckName}
              onChange={(e) => setDeckName(e.target.value)}
              placeholder="Enter deck name"
            />
          </div>
        }
        footer={
          <>
            <button className="btn btn-secondary-outline" onClick={handleClose}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleCreateDeck}>
              Create Deck
            </button>
          </>
        }
      />

      {/* Add Virtual Device Modal */}
      <GenericModal
        show={showAddVirtualDeviceModal}
        onClose={() => setShowAddVirtualDeviceModal(false)}
        header={
          <div className="custom-modal-header">
            <div className="modern-invite-icon">
              <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
              </svg>
            </div>
            <div className="deck-modal-header-content">
              <h2 className='slide1-main-title'>Create Virtual Kiosk</h2>
              <p className="modern-modal-subtitle">Enter a name for your new virtual kiosk.</p>
            </div>
          </div>
        }
        body={
          <div className="form-group">
            <div className="add-deck-label">Virtual Kiosk Name:</div>
            <input
              type="text"
              id="virtualKioskName"
              className="form-control"
              value={virtualKioskName}
              onChange={(e) => setVirtualKioskName(e.target.value)}
              placeholder="Enter virtual kiosk name"
            />
          </div>
        }
        footer={
          <>
            <button className="btn btn-secondary-outline" onClick={() => setShowAddVirtualDeviceModal(false)}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleCreateVirtualDevice}>
              Create
            </button>
          </>
        }
      />

      {/* View QR Code Modal - Dual Mode (Feedback + Polling) */}
      <GenericModal
        show={showQRModal}
        onClose={() => setShowQRModal(false)}
        header={
          <div className="custom-modal-header">
            <div className="modern-invite-icon">
              <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
              </svg>
            </div>
            <div className="deck-modal-header-content">
              <h2 className='slide1-main-title'>QR Codes for {qrDeviceName}</h2>
              <p className="modern-modal-subtitle">
                {pollingConfigExists ? 'Scan to access feedback or polling.' : 'Scan for customer feedback.'}
              </p>
            </div>
          </div>
        }
        body={
          <>
            {/* QR Type Tabs - Only show polling tab if polling config exists */}
            <div className="qr-type-tabs" style={{
              display: 'flex',
              gap: '8px',
              marginBottom: '20px',
              borderBottom: '2px solid #e5e7eb'
            }}>
              <button
                className={`qr-tab-button ${qrType === 'feedback' ? 'active' : ''}`}
                onClick={() => setQrType('feedback')}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  background: qrType === 'feedback' ? '#667eea' : 'transparent',
                  color: qrType === 'feedback' ? 'white' : '#666',
                  cursor: 'pointer',
                  borderRadius: '4px 4px 0 0',
                  fontWeight: qrType === 'feedback' ? '600' : '500',
                  transition: 'all 0.3s'
                }}
              >
                📋 Feedback
              </button>

              {/* Polling Tab - Only show if polling configuration exists */}
              {pollingConfigExists && (
                <button
                  className={`qr-tab-button ${qrType === 'polling' ? 'active' : ''}`}
                  onClick={() => setQrType('polling')}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    background: qrType === 'polling' ? '#667eea' : 'transparent',
                    color: qrType === 'polling' ? 'white' : '#666',
                    cursor: 'pointer',
                    borderRadius: '4px 4px 0 0',
                    fontWeight: qrType === 'polling' ? '600' : '500',
                    transition: 'all 0.3s'
                  }}
                >
                  📊 Polling
                </button>
              )}
            </div>

            {/* Show message if polling is not configured */}
            {!pollingConfigExists && qrType === 'polling' && (
              <div style={{
                padding: '12px',
                backgroundColor: '#fef3c7',
                borderLeft: '4px solid #f59e0b',
                borderRadius: '4px',
                marginBottom: '16px'
              }}>
                <p style={{ margin: '0', fontSize: '14px', color: '#92400e' }}>
                  <strong>⚠️ Polling Not Configured:</strong>
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#b45309' }}>
                  Polling is not yet configured for this deck. Please set up a polling configuration in the Polling Slide section to enable polling QR codes.
                </p>
              </div>
            )}

            {/* QR Code Display */}
            <div className="qr-code-container-wrapper">
              <div className="qr-code-container" ref={qrCodeRef}>
                <QRCode
                  value={qrType === 'feedback' ? qrCodeUrl : pollingQRCodeUrl}
                  size={256}
                />
              </div>
            </div>

            {/* Mode Description */}
            {(qrType === 'feedback' || (qrType === 'polling' && pollingConfigExists)) && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: '#f0f9ff',
                borderLeft: '4px solid #667eea',
                borderRadius: '4px'
              }}>
                <p style={{ margin: '0', fontSize: '14px', color: '#333' }}>
                  <strong>{qrType === 'feedback' ? '📋 Feedback Mode:' : '📊 Polling Mode:'}</strong>
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: '#666' }}>
                  {qrType === 'feedback'
                    ? 'Customers scan this QR code to provide feedback and rate their experience.'
                    : 'Customers scan this QR code to participate in brand preference polling.'}
                </p>
              </div>
            )}

            {/* Polling Event Window - Show if polling is selected and config exists */}
            {qrType === 'polling' && pollingConfigExists && (
              <>
                {!isEditingDates ? (
                  <div style={{ marginTop: '12px', padding: '10px 12px', background: '#f0fdf4', borderLeft: '4px solid #10b981', borderRadius: '4px', cursor: 'pointer' }} onClick={() => setIsEditingDates(true)}>
                    <p style={{ margin: 0, fontSize: '13px', color: '#065f46' }}>
                      <strong>📅 Event Window:</strong>
                    </p>
                    <p style={{ margin: '6px 0 0', fontSize: '12px', color: '#047857' }}>
                      {pollingEventDates.startDate
                        ? `Starts: ${new Date(pollingEventDates.startDate).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}`
                        : 'No start date set'}
                      {' · '}
                      {pollingEventDates.endDate
                        ? `Expires: ${new Date(pollingEventDates.endDate).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}`
                        : 'No expiry set'}
                    </p>
                    <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: '#059669', fontStyle: 'italic' }}>
                      Click to edit
                    </p>
                  </div>
                ) : (
                  <div style={{ marginTop: '12px', padding: '12px', background: '#f0fdf4', borderLeft: '4px solid #10b981', borderRadius: '4px' }}>
                    <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#065f46' }}>
                      <strong>📅 Set Event Window:</strong>
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#333' }}>
                          Starts
                        </label>
                        <input
                          type="datetime-local"
                          value={editableDates.startDate}
                          onChange={(e) => setEditableDates({ ...editableDates, startDate: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            fontSize: '12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontFamily: 'inherit'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '12px', marginBottom: '4px', color: '#333' }}>
                          Expires
                        </label>
                        <input
                          type="datetime-local"
                          value={editableDates.endDate}
                          onChange={(e) => setEditableDates({ ...editableDates, endDate: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            fontSize: '12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '4px',
                            fontFamily: 'inherit'
                          }}
                        />
                      </div>
                      {editableDates.startDate && editableDates.endDate &&
                       new Date(editableDates.startDate) > new Date(editableDates.endDate) && (
                        <p style={{ fontSize: '12px', color: '#dc2626', margin: '0' }}>
                          ⚠️ End date must be after start date
                        </p>
                      )}
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => {
                            setIsEditingDates(false);
                            setEditableDates({
                              startDate: pollingEventDates.startDate ? new Date(pollingEventDates.startDate).toISOString().slice(0, 16) : '',
                              endDate: pollingEventDates.endDate ? new Date(pollingEventDates.endDate).toISOString().slice(0, 16) : ''
                            });
                          }}
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            background: '#e5e7eb',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: '500'
                          }}
                          disabled={isSavingDates}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSavePollingDates}
                          style={{
                            padding: '6px 12px',
                            fontSize: '12px',
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: '500'
                          }}
                          disabled={isSavingDates}
                        >
                          {isSavingDates ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="qr-modal-footer" style={{ marginTop: '20px' }}>
              <div className="qr-actions">
                <button
                  className="btn btn-primary btn-small"
                  onClick={() => QRCodeExportUtils.downloadPNG(qrCodeRef, `${qrDeviceName}-${qrType}`)}
                  title="Download as PNG"
                >
                  <FiDownload />
                </button>
                <button
                  className="btn btn-primary btn-small"
                  onClick={() => QRCodeExportUtils.downloadPDF(qrCodeRef, `${qrDeviceName}-${qrType}`)}
                  title="Download as PDF"
                >
                  <FiFileText />
                </button>
                <button
                  className="btn btn-primary btn-small"
                  onClick={() => QRCodeExportUtils.printQRCode(qrCodeRef, `${qrDeviceName}-${qrType}`)}
                  title="Print QR Code"
                >
                  <FiPrinter />
                </button>
              </div>
            </div>
          </>
        }
        footer={
          <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <Button className="btn btn-secondary" size="small" onClick={() => setShowQRModal(false)}>
              Close
            </Button>
          </div>
        }
      />

      {/* Add Business Modal */}
      <GenericModal
        show={showAddBusinessModal}
        onClose={() => setShowAddBusinessModal(false)}
        header={
          <div className="custom-modal-header">
            <div className="modern-invite-icon">
              <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4"/>
              </svg>
            </div>
            <div className="deck-modal-header-content">
              <h2 className='slide1-main-title'>Add New Business</h2>
              <p className="modern-modal-subtitle">Enter the details for the new business.</p>
            </div>
          </div>
        }
        body={
          <div className="form-group">
            <div className="add-deck-label">Business Name:</div>
            <input
              type="text"
              id="businessName"
              className="form-control"
              value={newBusinessName}
              onChange={(e) => setNewBusinessName(e.target.value)}
              placeholder="Enter business name"
            />
          </div>
        }
        footer={
          <>
            <button className="btn btn-secondary-outline" onClick={() => setShowAddBusinessModal(false)}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleAddBusiness}>
              Add Business
            </button>
          </>
        }
      />
    </div>
  );
};

export default BusinessStoreSelector;